import { useState } from "react";
import { Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useArchiveMerchValue } from "@/lib/api/risk/merchValues";

interface ArchiveMerchValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchValueId: string;
}

export function ArchiveMerchValueDialog({
  open,
  onOpenChange,
  merchValueId,
}: ArchiveMerchValueDialogProps) {
  const { toast } = useToast();
  const archiveMerchValue = useArchiveMerchValue();
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    if (isArchiving) return;

    setIsArchiving(true);
    try {
      const result = await archiveMerchValue(merchValueId);

      if (result.success) {
        toast({ title: "Success", description: "Merch value archived successfully" });
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to archive merch value",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isArchiving) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-yellow-600" />
            Archive Merch Value
          </DialogTitle>
          <DialogDescription className="pt-2">
            Are you sure you want to archive this merch value? It will no longer
            appear in the active list but can be viewed in History.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isArchiving}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
            onClick={handleArchive}
            disabled={isArchiving}
          >
            {isArchiving ? "Archiving..." : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
